import { fetchClobServerTimeSec } from '@/lib/polymarket/fetchClobServerTime.js';

/** CLOB /time 相对本地时钟的偏移（serverMs - localMs） */
let offsetMs = 0;
/** 最近一次 /time 目标偏移；展示时钟逐步收敛到此值，避免 resync 猛跳 */
let targetOffsetMs = 0;
/** 最近一次同步时的本地毫秒 */
let syncedAtLocalMs = 0;
/** 最近一次接口返回的 Unix 秒 */
let lastServerSec = 0;

/** 每 tick 向 targetOffset 收敛的最大步幅（毫秒） */
export const CLOB_OFFSET_CONVERGE_STEP_MS = 80;
/** 与当前偏移差超过此值时立即对齐（本机钟大幅变动） */
export const CLOB_OFFSET_HARD_RESYNC_MS = 3000;
/** 倒计时 tick 最小间隔，避免毫秒级连跳 */
export const CLOB_COUNTDOWN_TICK_MIN_MS = 50;

/**
 * 记录 CLOB /time 返回值，校准后续 getClobNow*。
 * @param {number} serverSec
 * @param {number} [localMs]
 */
export function recordClobServerTime(serverSec, localMs = Date.now()) {
  const sec = Number(serverSec);
  const local = Number(localMs);
  if (!Number.isFinite(sec) || sec <= 0 || !Number.isFinite(local)) return false;

  lastServerSec = Math.floor(sec);
  const rawOffset = lastServerSec * 1000 - local;
  targetOffsetMs = rawOffset;

  if (syncedAtLocalMs === 0) {
    offsetMs = rawOffset;
  } else if (Math.abs(rawOffset - offsetMs) > CLOB_OFFSET_HARD_RESYNC_MS) {
    const prevOffset = offsetMs;
    offsetMs = rawOffset;
    if (typeof console !== 'undefined' && console.info) {
      console.info('[crypto-updown] event=clob_clock_hard_resync', {
        deltaMs: rawOffset - prevOffset,
        targetOffsetMs: rawOffset,
      });
    }
  }

  syncedAtLocalMs = local;
  return true;
}

/**
 * 展示时钟向最近一次 /time 目标偏移渐进收敛（每秒 tick 调用一次即可）。
 * @param {number} [maxStepMs]
 */
export function convergeClobClockOffset(maxStepMs = CLOB_OFFSET_CONVERGE_STEP_MS) {
  const step = Math.max(1, Number(maxStepMs) || CLOB_OFFSET_CONVERGE_STEP_MS);
  const diff = targetOffsetMs - offsetMs;
  if (Math.abs(diff) <= step) {
    offsetMs = targetOffsetMs;
    return;
  }
  offsetMs += diff > 0 ? step : -step;
}

export function getClobClockOffsetMs() {
  return offsetMs;
}

export function getClobClockTargetOffsetMs() {
  return targetOffsetMs;
}

export function getLastClobServerTimeSec() {
  return lastServerSec;
}

export function getClobClockSyncedAtLocalMs() {
  return syncedAtLocalMs;
}

export function hasClobClockSync() {
  return syncedAtLocalMs > 0 && lastServerSec > 0;
}

/** 校准后的当前毫秒（未同步时等同 Date.now） */
export function getClobNowMs() {
  return Date.now() + offsetMs;
}

/** 校准后的当前 Unix 秒 */
export function getClobNowSec() {
  return Math.floor(getClobNowMs() / 1000);
}

/**
 * 分钟盘倒计时展示滞后：相对 CLOB 真时间故意慢一拍，
 * 避免比 PM 更早翻秒 / 开滚（PM 页面时钟通常略慢于瞬时 /time）。
 */
export const UPDOWN_COUNTDOWN_DISPLAY_LAG_MS = 950;

/** 倒计时 / 切盘展示用的「慢半拍」校准时间 */
export function getClobCountdownNowMs(lagMs = UPDOWN_COUNTDOWN_DISPLAY_LAG_MS) {
  const lag = Math.max(0, Number(lagMs) || 0);
  return getClobNowMs() - lag;
}

/** 距下一个校准整秒的毫秒（用于墙钟对齐） */
export function msUntilNextClobSecond(nowMs = getClobNowMs()) {
  const now = Number(nowMs);
  if (!Number.isFinite(now)) return 1000;
  const mod = ((now % 1000) + 1000) % 1000;
  return mod === 0 ? 1000 : 1000 - mod;
}

/**
 * 距倒计时「剩余秒」下一次 floor 变化的毫秒。
 * floor(remaining/1000) 在 remaining 降到 N*1000 以下时减 1；
 * 例如剩余 5.48s → ~481ms 后变为 4s，避免 setInterval 半秒就开滚。
 */
export function msUntilNextRemainingSecondBoundary(endSec, nowMs = getClobNowMs()) {
  const endMs = Number(endSec) * 1000;
  const now = Number(nowMs);
  if (!Number.isFinite(endMs) || endMs <= 0 || !Number.isFinite(now)) {
    return msUntilNextClobSecond(now);
  }
  const remaining = endMs - now;
  if (remaining <= 0) return msUntilNextClobSecond(now);
  const floorSec = Math.floor(remaining / 1000);
  if (floorSec <= 0) return Math.max(CLOB_COUNTDOWN_TICK_MIN_MS, Math.ceil(remaining));
  // 当 remaining < floorSec*1000 时显示值减 1
  const delay = remaining - floorSec * 1000 + 1;
  return Math.max(CLOB_COUNTDOWN_TICK_MIN_MS, Math.ceil(delay));
}

/**
 * 拉取 CLOB /time 并写入偏移。
 * @returns {Promise<number>} 校准后的当前 Unix 秒
 */
export async function syncClobServerClock() {
  const localBefore = Date.now();
  const serverSec = await fetchClobServerTimeSec();
  const localAfter = Date.now();
  // 用 RTT 中点降低网络延迟偏差
  const localMid = localBefore + Math.floor((localAfter - localBefore) / 2);
  recordClobServerTime(serverSec, localMid);
  return getClobNowSec();
}
